/**
 * FIN-12351 — the two `groupingKeyToValueMapping` rules the backend enforces, mirrored here so a
 * variant cannot be saved into a rejection.
 *
 * Both are verbatim mirrors of `FraudAggregationFunction.scala` `validate`:
 *
 *  G1  every mapping KEY must be one of that function's own `groupingKeys`, compared as an EXACT
 *      string. Reached from the UI by deselecting a grouping key while its redirect survives —
 *      a Group By edit that looks unrelated to the mapping. BE message:
 *      "groupingKeyToValueMapping keys … are not grouping keys of indicator '<name>'".
 *
 *  G2  a non-empty mapping is not combinable with `includeCurrentTransaction = true`. One gate,
 *      two distinct reasons — which is why the message has to cover both:
 *
 *      SAME-CLASS (applicableTo == the rule's inputType). The current event sits on the UNMAPPED
 *      axis: a `{receiver -> sender}` read is "incoming of the current sender", but the current
 *      event is one the sender SENT. Folding it in would count an outgoing event towards an
 *      incoming total — a direction error, not a precompute limitation.
 *
 *      CROSS-CLASS (applicableTo != inputType, e.g. count activity on a transaction rule). The
 *      current event is not of the counted class at all, so it was never in the aggregate:
 *      FraudRealtimeAggregationFunction filters state to `applicableTo` BEFORE applying this flag.
 *      Unchecking changes no number — the flag is refused because a control that reads as
 *      meaningful and does nothing invites a threshold tuned around a contribution never there.
 *
 * Same shape and discipline as `includeCurrentTransactionValidation.js`, which guards the drawer's
 * other contradictory-config case.
 */

const mappingOf = (args) => (args && args.groupingKeyToValueMapping) || {};

/** G1 — the mapping keys that are not grouping keys of this function (empty = valid). */
export const mappingKeysNotGroupingKeys = (args) => {
  const keys = new Set((args && args.groupingKeys) || []);
  return Object.keys(mappingOf(args)).filter((key) => !keys.has(key));
};

/** G2 — a redirect together with "Include Current". */
export const isMappingIncludeCurrentConflict = (args) =>
  Object.keys(mappingOf(args)).length > 0 &&
  (args || {}).includeCurrentTransaction === true;

/**
 * Inline hint shown live on the function card while G2 is violated.
 *
 * Why G2 exists (kept out of the UI on purpose, FIN-14013): the Perspective redirects the
 * grouping key to the other party, leaving the current event on the side that is NOT redirected.
 * If the function counts the SAME event class, folding the current event in would add it to the
 * opposite direction (an outgoing event landing in an incoming total). If it counts the OTHER
 * class, the current event was never counted in the first place. Either way, unchecking
 * "Include Current" does not change any count.
 */
export const MAPPING_INCLUDE_CURRENT_HINT =
  '"Include Current" cannot be used with a Perspective. Uncheck it to save.';

/**
 * @returns {{name: string, kind: 'mapping-key'|'include-current'}[]} one entry per offending
 * aggregation function (empty = no violation).
 */
export const findMappingViolations = (record) =>
  ((record && record.function) || []).reduce((acc, fn) => {
    const args = fn && fn.args;
    if (!args) return acc;
    const name = fn.name || '(unnamed)';
    if (mappingKeysNotGroupingKeys(args).length > 0) {
      acc.push({ name, kind: 'mapping-key' });
    }
    if (isMappingIncludeCurrentConflict(args)) {
      acc.push({ name, kind: 'include-current' });
    }
    return acc;
  }, []);

/** Build the save-block message for the offending functions, or null when there is none. */
export const mappingViolationMessage = (violations) => {
  if (!violations || violations.length === 0) return null;

  const staleKeys = violations
    .filter((v) => v.kind === 'mapping-key')
    .map((v) => v.name);
  const includeCurrent = violations
    .filter((v) => v.kind === 'include-current')
    .map((v) => v.name);

  const parts = [];
  if (staleKeys.length > 0) {
    parts.push(
      `${
        staleKeys.length > 1
          ? `Functions ${staleKeys.join(', ')} redirect`
          : `Function ${staleKeys[0]} redirects`
      } a Group By field that is no longer selected. Re-open the function and set Perspective again — or, if no Perspective control is offered (its Group By fields have no configured pair), change a Group By key to clear the stale redirect.`
    );
  }
  if (includeCurrent.length > 0) {
    parts.push(
      `${
        includeCurrent.length > 1
          ? `Functions ${includeCurrent.join(', ')} combine`
          : `Function ${includeCurrent[0]} combines`
      } "Include Current" with a Perspective. Uncheck "Include Current" to save.`
    );
  }
  return parts.join(' ');
};
